import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { share as shareApi, diagrams as diagramsApi } from "@/lib/api";
import { parseDSL } from "@/lib/dsl-parser";
import DiagramRenderer from "@/components/editor/DiagramRenderer";
import { StickyNote } from "@/components/editor/StickyNote";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { GitFork, Code2, Wifi, WifiOff, Users, StickyNote as StickyNoteIcon, Lock, Unlock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";
import { useCollaboration } from "@/hooks/useCollaboration";

export default function Viewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [diagram, setDiagram] = useState(null);
  const [code, setCode] = useState("");
  const [parsedGraph, setParsedGraph] = useState(null);
  const [nodeColors, setNodeColors] = useState({});
  const [annotations, setAnnotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCode, setShowCode] = useState(false);
  const [addNoteMode, setAddNoteMode] = useState(false);
  const [canEditNotes, setCanEditNotes] = useState(true);
  const canvasRef = useRef(null);

  const token = useMemo(() => localStorage.getItem("auth_token"), []);

  // Live code update from collaborators
  const handleRemoteCodeChange = useCallback((remoteCode) => {
    setCode(remoteCode);
    try { setParsedGraph(parseDSL(remoteCode)); } catch {}
  }, []);

  // Live annotation update from collaborators
  const handleRemoteAnnotationsChange = useCallback((remoteAnnotations, remoteNodeColors) => {
    if (remoteAnnotations) setAnnotations(remoteAnnotations);
    if (remoteNodeColors) setNodeColors(remoteNodeColors);
  }, []);

  const { peers, connected, permissions, sendAnnotationsChange } = useCollaboration({
    diagramId: id,
    enabled: !!id,
    token,
    onRemoteCodeChange: handleRemoteCodeChange,
    onRemoteAnnotationsChange: handleRemoteAnnotationsChange,
  });

  // Update local permission when owner changes it
  useEffect(() => {
    setCanEditNotes(permissions.can_edit_notes || permissions.is_owner);
  }, [permissions]);

  // Initial load
  useEffect(() => {
    shareApi.get(id)
      .then((res) => {
        const data = res.data;
        setDiagram(data);
        setCode(data.dsl_code || "");
        setNodeColors(data.node_colors || {});
        setAnnotations(data.annotations || []);
        setCanEditNotes(data.collab_can_edit_notes !== false);
        try { setParsedGraph(parseDSL(data.dsl_code)); } catch {}
      })
      .catch(() => toast.error("Diagram not found or not public"))
      .finally(() => setLoading(false));
  }, [id]);

  // Re-parse on code change
  useEffect(() => {
    if (!code) return;
    try { setParsedGraph(parseDSL(code)); } catch {}
  }, [code]);

  const handleFork = async () => {
    if (!user) { navigate("/auth"); return; }
    try {
      const res = await diagramsApi.fork(id);
      navigate(`/editor/${res.data.id}`);
      toast.success("Diagram forked — edit it now!");
    } catch { toast.error("Failed to fork"); }
  };

  const handleOpenEditor = () => {
    if (!user) { navigate("/auth"); return; }
    navigate(`/editor/${id}`);
  };

  // Add note on canvas click
  const handleCanvasClick = useCallback((e) => {
    if (!addNoteMode || !canEditNotes) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const newNote = {
      id: `note_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type: "sticky",
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
      text: "",
      color: "#fef08a",
      node_id: null,
    };
    const updated = [...annotations, newNote];
    setAnnotations(updated);
    setAddNoteMode(false);
    sendAnnotationsChange(updated, nodeColors);
    toast.success("Note added — double-click to edit");
  }, [addNoteMode, canEditNotes, annotations, nodeColors, sendAnnotationsChange]);

  const handleNoteUpdate = useCallback((updated) => {
    if (!canEditNotes) return;
    const newAnnotations = annotations.map(a => a.id === updated.id ? updated : a);
    setAnnotations(newAnnotations);
    sendAnnotationsChange(newAnnotations, nodeColors);
  }, [canEditNotes, annotations, nodeColors, sendAnnotationsChange]);

  const handleNoteDelete = useCallback((noteId) => {
    if (!canEditNotes) return;
    const newAnnotations = annotations.filter(a => a.id !== noteId);
    setAnnotations(newAnnotations);
    sendAnnotationsChange(newAnnotations, nodeColors);
  }, [canEditNotes, annotations, nodeColors, sendAnnotationsChange]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm font-body">
        Loading...
      </div>
    );
  }

  if (!diagram) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center text-slate-400 text-sm font-body">
          Diagram not found or not publicly shared
        </div>
      </div>
    );
  }

  const freeNotes = annotations.filter(a => !a.node_id);
  const anchoredNotes = annotations.filter(a => a.node_id);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="viewer-page">
      <Header />
      <div className="flex-1 flex flex-col" style={{ minHeight: 0 }}>

        {/* Toolbar */}
        <div className="px-6 md:px-10 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 font-heading" data-testid="viewer-title">
              {diagram.title}
            </h1>
            <p className="text-xs text-slate-400 font-body">
              by {diagram.author?.username || diagram.author?.email || "Unknown"}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Live indicator */}
            <div
              data-testid="viewer-collab-status"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                connected ? "bg-green-50 border-green-200 text-green-700" : "bg-amber-50 border-amber-200 text-amber-700"
              }`}
            >
              {connected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {connected ? "Live" : "Reconnecting..."}
              {peers.length > 0 && (
                <span className="flex items-center gap-1 ml-1">
                  <Users className="h-3 w-3" />
                  {peers.length + 1}
                </span>
              )}
            </div>

            {/* Notes permission badge */}
            <div
              data-testid="viewer-notes-permission"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                canEditNotes ? "bg-amber-50 border-amber-200 text-amber-700" : "bg-slate-50 border-slate-200 text-slate-500"
              }`}
              title={canEditNotes ? "You can add and edit notes" : "Note editing is disabled by the owner"}
            >
              {canEditNotes ? <Unlock className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
              {canEditNotes ? "Notes: on" : "Notes: off"}
            </div>

            {/* Add note button — only when allowed */}
            {canEditNotes && (
              <Button
                variant={addNoteMode ? "default" : "outline"}
                size="sm"
                className="gap-1.5 h-8 text-xs"
                onClick={() => setAddNoteMode(v => !v)}
                data-testid="viewer-add-note-btn"
              >
                <StickyNoteIcon className="h-3.5 w-3.5" />
                {addNoteMode ? "Click canvas..." : "Add Note"}
              </Button>
            )}

            <Button variant="outline" size="sm" onClick={() => setShowCode(!showCode)} data-testid="toggle-code-btn">
              <Code2 className="h-3.5 w-3.5 mr-1.5" /> {showCode ? "Hide Code" : "View Code"}
            </Button>
            <Button variant="outline" size="sm" onClick={handleFork} data-testid="fork-btn">
              <GitFork className="h-3.5 w-3.5 mr-1.5" /> Fork
            </Button>
            {user && (
              <Button size="sm" onClick={handleOpenEditor} data-testid="open-editor-btn">
                Open in Editor
              </Button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex" style={{ minHeight: 0 }}>
          {showCode && (
            <div className="w-80 border-r border-slate-200 p-5 overflow-auto bg-slate-50 shrink-0">
              <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mb-3">DSL Source</p>
              <pre className="text-[12px] text-slate-600 leading-relaxed font-mono whitespace-pre-wrap" data-testid="viewer-code">
                {code}
              </pre>
            </div>
          )}

          {/* Canvas */}
          <div
            ref={canvasRef}
            className="flex-1 bg-[#fafbfc] overflow-hidden relative"
            style={{
              backgroundImage: "radial-gradient(circle, #e2e8f0 1px, transparent 1px)",
              backgroundSize: "20px 20px",
              cursor: addNoteMode ? "cell" : "default",
            }}
            data-testid="viewer-canvas"
            onClick={handleCanvasClick}
          >
            <DiagramRenderer
              graph={parsedGraph}
              nodeColors={nodeColors}
              annotations={anchoredNotes}
              readonly
            />

            {/* Free-floating sticky notes */}
            {freeNotes.map(note => (
              <StickyNote
                key={note.id}
                note={note}
                onUpdate={canEditNotes ? handleNoteUpdate : undefined}
                onDelete={canEditNotes ? handleNoteDelete : undefined}
                readonly={!canEditNotes}
              />
            ))}

            {/* Add-note instruction overlay */}
            {addNoteMode && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="bg-amber-50 border border-amber-300 text-amber-700 text-sm font-medium px-4 py-2 rounded-full shadow">
                  Click anywhere to place a sticky note
                </div>
              </div>
            )}

            {/* Peer avatars */}
            {peers.length > 0 && (
              <div
                data-testid="viewer-peers"
                className="absolute top-3 right-3 flex items-center gap-1"
              >
                {peers.map(peer => (
                  <div
                    key={peer.session_id}
                    title={peer.name}
                    style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: peer.color, border: "2px solid white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "white", fontSize: 11, fontWeight: 700,
                      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                    }}
                  >
                    {peer.name?.[0]?.toUpperCase() || "G"}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
